import { useEffect, useRef, useState } from 'react';
import { usePreferencesStore } from '../store/preferencesStore';
import { useAuthStore } from '../store/authStore';
import {
  useWorkspaceStore,
  setWorkspaces,
  setCurrentWorkspace,
  setIsLoadingWorkspaces,
} from '../store/workspaceStore';
import { setTeams } from '../store/teamsStore';
import { loadHistoryForCurrentNamespace } from '../services/historyPersistence';
import {
  setEnvironments,
  setSelectedEnvironment,
  setGlobalVariables,
} from '../store/variablesStore';
import { workspaceService } from '../services/workspaceService';
import teamsService from '../services/teamsService';
import {
  saveSnapshot,
  saveLocalWorkspaceManifest,
  listLocalWorkspaceMetasResolved,
  adoptLocalNamespaceWorkspaces,
} from '../services/localWorkspaceStore';
import { apiClientKey } from '../services/storageNamespace';
import {
  generateLocalWorkspaceId,
  buildLocalManifestEntries,
  mergeRemoteAndLocalWorkspaces,
} from '../utils/workspaceStorage';

/**
 * App boot subsystem extracted verbatim from App (same effects, dep arrays and
 * relative order). Owns the splash/`wsTransition` machine + the workspaces-load
 * and guest→login merge effects. The shared loading state (initialLoadComplete /
 * isLoadingCollections / workspaceLoadKeyRef) stays App-owned (also used by
 * useCollectionsLoader + onLogoutReset) and is passed in. Returns the splash
 * flags the App shell renders.
 *
 * @param {{
 *   toastT: Function,
 *   initialLoadComplete: boolean,
 *   setInitialLoadComplete: Function,
 *   workspaceLoadKeyRef: { current: any },
 *   isLoadingCollections: boolean,
 * }} deps
 * @returns {{ splashVisible: boolean, splashFading: boolean }}
 */
export function useWorkspaceBootstrap({
  toastT,
  initialLoadComplete,
  setInitialLoadComplete,
  workspaceLoadKeyRef,
  isLoadingCollections,
}) {
  const workspaceTransitionEnabled = usePreferencesStore((s) => s.workspaceTransition);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const isLoadingWorkspaces = useWorkspaceStore((s) => s.isLoadingWorkspaces);

  // Splash overlay — shown on cold start / Ctrl+R, fades out once workspace data is ready
  const [splashVisible, setSplashVisible] = useState(workspaceTransitionEnabled);
  const [splashFading, setSplashFading] = useState(false);
  // Tracks an in-progress workspace switch so the splash can be shown reliably
  // (tabsHydrating is too short-lived due to React 18 batching — see wsTransition effects below)
  const [wsTransition, setWsTransition] = useState(false);
  const prevWsIdRef = useRef(undefined);
  const prevAuthenticatedRef = useRef(undefined);

  // Show / hide the splash for initial load AND workspace switches.
  useEffect(() => {
    const shouldShow = workspaceTransitionEnabled && (!isInitialized || !initialLoadComplete || wsTransition);
    if (shouldShow) {
      setSplashVisible(true);
      setSplashFading(false);
    } else {
      setSplashFading(true);
      const t = setTimeout(() => setSplashVisible(false), 520);
      return () => clearTimeout(t);
    }
  }, [isInitialized, initialLoadComplete, wsTransition, workspaceTransitionEnabled]);

  // Detect a workspace switch → immediately raise wsTransition so the splash
  // appears in the very next render (avoids React-18 batching that collapses
  // tabsHydrating true→false into a single render).
  useEffect(() => {
    const nextId = currentWorkspace?.id ?? null;
    // Skip the very first mount (prevWsIdRef starts as undefined)
    if (prevWsIdRef.current !== undefined && prevWsIdRef.current !== nextId && initialLoadComplete && isInitialized) {
      setWsTransition(true);
    }
    prevWsIdRef.current = nextId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  // Lower wsTransition once collections have finished loading.
  // The 500 ms buffer handles the gap between workspace-ID change and
  // isLoadingCollections going true — without it the splash could dismiss
  // before the collections fetch even starts.
  useEffect(() => {
    if (!wsTransition) return;
    if (isLoadingCollections) return; // still busy — wait for the next run
    const t = setTimeout(() => setWsTransition(false), 500);
    return () => clearTimeout(t);
  }, [wsTransition, isLoadingCollections]);

  // Load workspaces when auth is ready (re-run guest → signed-in so AppData locals merge in)
  useEffect(() => {
    if (!isInitialized) return;

    const loadKey = `${isAuthenticated ? 'auth' : 'guest'}:${user?.id ?? 'none'}`;
    if (workspaceLoadKeyRef.current === loadKey && initialLoadComplete) {
      return;
    }

    const loadWorkspaces = async () => {
      if (isLoadingWorkspaces) return;

      if (isAuthenticated) {
        try {
          setIsLoadingWorkspaces(true);
          // Load workspaces from backend
          const userWorkspaces = await workspaceService.getWorkspaces();

          // Handle both old format (array) and new format (object with sections)
          // For backward compatibility, store as array in state
          const allWorkspaces = Array.isArray(userWorkspaces) ? userWorkspaces : (userWorkspaces?.all_workspaces || []);
          const manifestLocals = await listLocalWorkspaceMetasResolved();
          const mergedWorkspaces = mergeRemoteAndLocalWorkspaces(allWorkspaces, manifestLocals);
          setWorkspaces(mergedWorkspaces);

          // Load teams from backend — run in the background so it never blocks
          // the splash / initialLoadComplete. The Teams page fetches again on mount anyway.
          teamsService.getTeams()
            .then(userTeams => {
              const teamsArr = Array.isArray(userTeams) ? userTeams : (userTeams?.data ?? []);
              setTeams(teamsArr);
            })
            .catch(teamsError => {
              console.error('Failed to load teams:', teamsError);
              setTeams([]);
            });

          // Try to load current workspace from localStorage, or use first workspace
          const savedCurrentWorkspace = localStorage.getItem(apiClientKey('current-workspace'));
          if (savedCurrentWorkspace) {
            const currentWs = mergedWorkspaces.find(ws => ws.id.toString() === savedCurrentWorkspace);
            setCurrentWorkspace(currentWs || mergedWorkspaces[0] || null);
          } else {
            setCurrentWorkspace(mergedWorkspaces[0] || null);
          }
          workspaceLoadKeyRef.current = loadKey;
          setInitialLoadComplete(true);
        } catch (error) {
          console.error('Failed to load workspaces:', error);
          // Only show toast once, don't create an error loop
          if (error.message && !error.message.includes('Network Error')) {
            toastT('toast.loadWorkspacesFailed', 'error');
          }
          // Fallback to localStorage
          await loadWorkspacesFromLocalStorage();
          workspaceLoadKeyRef.current = loadKey;
          setInitialLoadComplete(true);
        } finally {
          setIsLoadingWorkspaces(false);
        }
      } else {
        // Not authenticated, use localStorage + manifest
        await loadWorkspacesFromLocalStorage();
        workspaceLoadKeyRef.current = loadKey;
        setInitialLoadComplete(true);
        setIsLoadingWorkspaces(false);
      }
    };

    const loadWorkspacesFromLocalStorage = async () => {
      try {
        const savedCurrentWorkspace = localStorage.getItem(apiClientKey('current-workspace'));
        const manifestLocals = await listLocalWorkspaceMetasResolved();

        const normalizeLocal = (w) =>
          !w.storage
            ? { ...w, storage: 'local', type: w.type || 'personal', role: 'owner', source: 'personal' }
            : w;

        let fromLs = [];
        try {
          const savedWorkspaces = localStorage.getItem(apiClientKey('workspaces'));
          if (savedWorkspaces) {
            fromLs = JSON.parse(savedWorkspaces)
              .map(normalizeLocal)
              .filter((w) => w.storage === 'local' || w.storage == null);
          }
        } catch {
          fromLs = [];
        }

        const merged = mergeRemoteAndLocalWorkspaces(fromLs, manifestLocals).map(normalizeLocal);

        if (merged.length > 0) {
          setWorkspaces(merged);
          localStorage.setItem(apiClientKey('workspaces'), JSON.stringify(merged));
          if (savedCurrentWorkspace) {
            const currentWs = merged.find((ws) => String(ws.id) === String(savedCurrentWorkspace));
            setCurrentWorkspace(currentWs || merged[0]);
          } else {
            setCurrentWorkspace(merged[0]);
          }
          return;
        }

        if (manifestLocals.length > 0) {
          const fromDisk = manifestLocals.map(normalizeLocal);
          setWorkspaces(fromDisk);
          localStorage.setItem(apiClientKey('workspaces'), JSON.stringify(fromDisk));
          const preferLocal =
            fromDisk.find((ws) => String(ws.id) === String(savedCurrentWorkspace)) || fromDisk[0];
          setCurrentWorkspace(preferLocal);
          return;
        }

        const localId = generateLocalWorkspaceId();
        const defaultWorkspace = {
          id: localId,
          local_uid: localId,
          name: 'Local',
          type: 'personal',
          storage: 'local',
          source: 'personal',
          role: 'owner',
          createdAt: Date.now(),
        };
        setWorkspaces([defaultWorkspace]);
        setCurrentWorkspace(defaultWorkspace);
        await saveSnapshot(localId, {
          collections: [],
          globalVariables: [],
          workspaceVariables: [],
          environments: [],
          selectedEnvironmentId: '',
        });
        await saveLocalWorkspaceManifest(buildLocalManifestEntries([defaultWorkspace]));
      } catch (error) {
        console.error('Failed to load workspaces from localStorage:', error);
        const localId = generateLocalWorkspaceId();
        const fallbackWorkspace = {
          id: localId,
          local_uid: localId,
          name: 'Local',
          type: 'personal',
          storage: 'local',
          source: 'personal',
          role: 'owner',
          createdAt: Date.now(),
        };
        setWorkspaces([fallbackWorkspace]);
        setCurrentWorkspace(fallbackWorkspace);
        try {
          await saveSnapshot(localId, {
            collections: [],
            globalVariables: [],
            workspaceVariables: [],
            environments: [],
            selectedEnvironmentId: '',
          });
          await saveLocalWorkspaceManifest(buildLocalManifestEntries([fallbackWorkspace]));
        } catch {
          // ignore
        }
      }
    };

    loadWorkspaces();
    // Every auth change that reaches here may have flipped the namespace.
    loadHistoryForCurrentNamespace();

    // Guest collections + variables load from per-workspace snapshot in loadCollections effect.

    // Load env/globals once per app session
    if (!initialLoadComplete) {
      const savedEnvironments = localStorage.getItem(apiClientKey('environments'));
      const savedSelectedEnvironment = localStorage.getItem(apiClientKey('selected-environment'));
      const savedGlobalVariables = localStorage.getItem(apiClientKey('global-variables'));

      if (savedEnvironments) {
        const envs = JSON.parse(savedEnvironments);
        setEnvironments(envs);

        if (savedSelectedEnvironment) {
          const selectedEnv = envs.find((env) => env.id === savedSelectedEnvironment);
          setSelectedEnvironment(selectedEnv || null);
        }
      } else {
        setEnvironments([]);
        setSelectedEnvironment(null);
      }

      if (savedGlobalVariables) {
        setGlobalVariables(JSON.parse(savedGlobalVariables));
      } else {
        setGlobalVariables([
          { key: 'timestamp', value: Date.now().toString(), enabled: true }
        ]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitialized, user?.id]);

  // After guest → login, merge remote workspaces with on-device manifest locals.
  useEffect(() => {
    const prevAuth = prevAuthenticatedRef.current;
    prevAuthenticatedRef.current = isAuthenticated;

    if (!(isAuthenticated && prevAuth === false) || !initialLoadComplete || !user) {
      return;
    }

    setWsTransition(true);

    let cancelled = false;

    const run = async () => {
      try {
        setIsLoadingWorkspaces(true);
        const userWorkspaces = await workspaceService.getWorkspaces();
        if (cancelled) return;
        const remoteFlat = Array.isArray(userWorkspaces)
          ? userWorkspaces
          : userWorkspaces?.all_workspaces || [];
        // Local-first hand-off. Work done while signed out lives in the
        // `_local` namespace; signing in is the explicit, user-driven moment
        // to adopt it. Each workspace is copied into THIS account's namespace
        // under a NEW id (never the `_local` id), leaving the signed-out
        // original intact. This is the only path by which a workspace crosses
        // a namespace boundary — nothing scans for strays anywhere else.
        try {
          await adoptLocalNamespaceWorkspaces(user.id);
        } catch (adoptErr) {
          console.warn('Failed to adopt signed-out workspaces:', adoptErr);
        }
        if (cancelled) return;
        const manifestLocals = await listLocalWorkspaceMetasResolved();
        if (cancelled) return;
        const merged = mergeRemoteAndLocalWorkspaces(remoteFlat, manifestLocals);
        setWorkspaces(merged);

        try {
          const userTeams = await teamsService.getTeams();
          const teamsArr = Array.isArray(userTeams) ? userTeams : (userTeams?.data ?? []);
          if (!cancelled) setTeams(teamsArr);
        } catch (teamsError) {
          console.error('Failed to load teams after auth:', teamsError);
          if (!cancelled) setTeams([]);
        }

        const savedCurrentWorkspace = localStorage.getItem(apiClientKey('current-workspace'));
        setCurrentWorkspace((cw) => {
          const match = merged.find((ws) => String(ws.id) === String(savedCurrentWorkspace));
          return match || cw || merged[0] || null;
        });
      } catch (error) {
        console.error('Failed to load workspaces after login:', error);
        if (!cancelled) toastT('toast.loadWorkspacesFailed', 'error');
      } finally {
        if (!cancelled) setIsLoadingWorkspaces(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, initialLoadComplete, user?.id]);

  return { splashVisible, splashFading };
}
